import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { useAIConfig } from './useAIConfig';
import { useAIChatMessages } from './useAIChatMessages';
import { useAIConversationStorage } from './useAIConversationStorage';
import { useAIChatSend } from './useAIChatSend';
import { useAIOpeningMessage } from './useAIOpeningMessage';
import {
    clearResetConversationMark,
    clearConversationLocalStorage,
    convKey,
    msgsKey
} from '../utils/aiChatUtils';

/**
 * AIチャット状態管理フック（統合）
 * hydrating: 履歴ロード中の「準備中」
 * openingFetching: opening 取得中の「準備中」
 *
 * @param {string} projectId - プロジェクトID
 * @param {object} user - ユーザーオブジェクト
 * @returns {object} チャット状態と操作関数
 */
export function useAIChatState(projectId, user, isWsConnected = false, waitForResult = null, reconnectWs = null) {
    const [conversationId, setConversationId] = useState("");
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [purging, setPurging] = useState(false);
    const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
    // 履歴ロード中の準備中表示
    const [hydrating, setHydrating] = useState(true);
    // opening 取得中の準備中表示
    const [openingFetching, setOpeningFetching] = useState(true);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [pendingInput, setPendingInput] = useState("");
    const [editingMessage, setEditingMessage] = useState(null);
    const [editContent, setEditContent] = useState("");

    const requestAbortRef = useRef(null);
    const messagesRef = useRef(messages);
    const textareaRef = useRef(null);
    const formRef = useRef(null);
    const previousModelTypeRef = useRef(null);

    useEffect(() => {
        messagesRef.current = messages;
    }, [messages]);

    const {
        aiConfig,
        checkingAiConfig,
        aiConfigError
    } = useAIConfig(projectId);

    const storage = useAIConversationStorage(
        user,
        conversationId,
        messages,
        setConversationId,
        setMessages
    );
    const { deletedMsgsKey } = storage;

    useAIChatMessages(
        projectId,
        conversationId,
        user,
        setMessages,
        setHydrating,
        setHistoryLoading,
        convKey,
        msgsKey,
        deletedMsgsKey,
        messagesRef,
        setConversationId
    );

    useAIOpeningMessage(
        projectId,
        user?.login_id,
        historyLoading,
        messages,
        setMessages,
        aiConfig,
        aiConfigError,
        previousModelTypeRef,
        setOpeningFetching
    );

    const hasTurnLimit = useMemo(() => {
        return aiConfig?.function_model_id === 'disaster_tankyu';
    }, [aiConfig?.function_model_id]);

    const MAX_TURNS = useMemo(() => {
        if (!hasTurnLimit) return 8;
        return 8;
    }, [hasTurnLimit]);

    const currentTurn = useMemo(() => {
        const userMessages = messages.filter(m => m.role === "user");
        return userMessages.length;
    }, [messages]);

    const isTurnLimitReached = useMemo(() => {
        if (!hasTurnLimit) return false;
        return currentTurn >= MAX_TURNS;
    }, [hasTurnLimit, currentTurn, MAX_TURNS]);

    const { sendMessage, sendMessageDirectly, requestGenRef } = useAIChatSend(
        projectId,
        conversationId,
        setConversationId,
        messages,
        setMessages,
        setInput,
        setPendingInput,
        setLoading,
        setShowLoadingIndicator,
        setError,
        messagesRef,
        requestAbortRef,
        MAX_TURNS,
        textareaRef,
        loading,
        purging,
        aiConfig,
        isWsConnected,
        waitForResult,
        reconnectWs
    );

    const handleEditMessage = useCallback((message) => {
        if (message.role !== "user") return;
        if (loading || purging) return;
        setEditingMessage(message);
        setEditContent(message.content);
    }, [loading, purging]);

    const handleConfirmEdit = useCallback(async () => {
        if (!editingMessage || !editContent.trim()) return;
        if (loading || purging) return;

        const targetIndex = messages.findIndex(m => {
            return m.role === editingMessage.role && m.id === editingMessage.id;
        });
        if (targetIndex === -1) {
            console.error("[useAIChatState] Cannot find message to edit");
            setEditingMessage(null);
            setEditContent("");
            return;
        }

        const targetTurn = editingMessage.turn ?? undefined;
        const messagesBeforeEdit = messages.slice(0, targetIndex);
        const messagesToDelete = messages.slice(targetIndex);

        if (conversationId && user?.login_id) {
            try {
                const deletedKey = deletedMsgsKey(user.login_id, conversationId);
                if (deletedKey) {
                    const existingDeleted = localStorage.getItem(deletedKey);
                    const deletedIds = existingDeleted ? JSON.parse(existingDeleted) : [];
                    const newDeletedIds = messagesToDelete
                        .map(m => m.id)
                        .filter(id => id && !id.startsWith('temp-') && !id.startsWith('opening-'));
                    const updatedDeletedIds = [...new Set([...deletedIds, ...newDeletedIds])];
                    localStorage.setItem(deletedKey, JSON.stringify(updatedDeletedIds));
                }
            } catch (_) {
                // noop
            }
        }

        setEditingMessage(null);
        const editedContent = editContent;
        setEditContent("");

        await sendMessageDirectly(editedContent, targetTurn, messagesBeforeEdit);
    }, [
        editingMessage,
        editContent,
        messages,
        conversationId,
        user?.login_id,
        deletedMsgsKey,
        sendMessageDirectly,
        loading,
        purging
    ]);

    const handleNewConversation = useCallback(async () => {
        try {
            if (loading && requestAbortRef.current) {
                try { requestAbortRef.current.abort(); } catch (_) { /* noop */ }
            }
            setPurging(true);
            requestGenRef.current++;

            const cidToDelete =
                conversationId ||
                (user?.login_id ? (localStorage.getItem(convKey(user.login_id)) || "") : "");

            if (user?.login_id) {
                clearConversationLocalStorage(user.login_id);
            }

            setConversationId("");
            setMessages([]);
            setOpeningFetching(true);
            setHydrating(false);
            setHistoryLoading(false);
            setPurging(false);

            if (cidToDelete && projectId) {
                import('../../../utils/api')
                    .then(({ deleteAIConversation }) => deleteAIConversation(cidToDelete))
                    .catch(() => {});
            }
        } finally {
            clearResetConversationMark();
        }
    }, [
        loading,
        conversationId,
        projectId,
        user?.login_id,
        requestGenRef
    ]);

    return {
        conversationId,
        messages,
        input,
        loading,
        error,
        purging,
        showLoadingIndicator,
        hydrating,
        openingFetching,
        historyLoading,
        pendingInput,
        editingMessage,
        editContent,
        aiConfig,
        checkingAiConfig,
        aiConfigError,
        MAX_TURNS,
        hasTurnLimit,
        currentTurn,
        isTurnLimitReached,

        setInput,
        setMessages,
        setPendingInput,
        setError,
        setEditingMessage,
        setEditContent,

        sendMessage,
        sendMessageDirectly,
        handleEditMessage,
        handleConfirmEdit,
        handleNewConversation,

        messagesRef,
        textareaRef,
        formRef,
        requestAbortRef,
        requestGenRef
    };
}
